<template>
  <div :style="containerStyle" class="tw:w-full">
    <a-select
      v-model:value="selectValue"
      :class="selectClass"
      :style="selectStyle"
      v-bind="mergedAttrs"
      @change="handleChange"
      @blur="handleBlur"
      @search="handleSearch"
      :dropdownMatchSelectWidth="false"
    >
      <template #suffixIcon>
        <slot v-if="$slots.suffixIcon" name="suffixIcon" />
        <IconChevronDown v-else class="select-arrow-icon" width="16px" height="16px" />
      </template>
      <template #notFoundContent>
        <slot v-if="$slots.notFoundContent" name="notFoundContent" />
        <div v-else class="tw:flex tw:flex-col tw:items-center tw:justify-center">
          <IconEmpty class="tw:w-32 tw:h-32" />
          <div class="tw:mt-8 tw:text-1 tw:text-s-d-s-dark-text-default-tertiary">No Data</div>
        </div>
      </template>
    </a-select>
  </div>
</template>

<script setup lang="ts">
import { computed, normalizeClass, useAttrs, type HTMLAttributes } from 'vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import IconEmpty from '@/components/icons.new/IconEmpty.vue'
interface SelectOption {
  label: string
  value: string | number
  disabled?: boolean
}

interface Props {
  modelValue?: string | number | (string | number)[]
  placeholder?: string
  disabled?: boolean
  size?: 'large' | 'middle' | 'small'
  width?: string
  height?: string
  options?: SelectOption[] | Record<string, unknown>[]
  mode?: 'multiple' | 'tags'
  allowClear?: boolean
  showSearch?: boolean
  filterOption?: boolean | ((input: string, option: SelectOption) => boolean)
  borderless?: boolean
  /**
   * Keep `borderless`'s translucent fill but draw the 1px hairline border the
   * SDS form controls use. `borderless` on its own strips the border outright,
   * which no longer matches the Style dialog's selects.
   */
  softBordered?: boolean
  rounded?: boolean
  class?: HTMLAttributes['class']
  itemTitle?: string
  itemValue?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  placeholder: '',
  disabled: false,
  size: 'middle',
  width: undefined,
  height: undefined,
  options: () => [],
  mode: undefined,
  allowClear: false,
  showSearch: false,
  filterOption: true,
  borderless: false,
  softBordered: false,
  rounded: false,
  class: '',
  itemTitle: 'label',
  itemValue: 'value',
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | (string | number)[] | undefined]
  change: [value: string | number | (string | number)[], option: SelectOption | SelectOption[]]
  blur: [e: FocusEvent]
  search: [value: string]
}>()

const attrs = useAttrs()

const selectValue = computed({
  get: () => {
    // Convert empty string to undefined so placeholder can display
    if (props.modelValue === '' || props.modelValue === null) {
      return undefined
    }
    // For arrays, filter out empty strings
    if (Array.isArray(props.modelValue)) {
      const filtered = props.modelValue.filter((v) => v !== '' && v !== null)
      return filtered.length > 0 ? filtered : undefined
    }
    return props.modelValue
  },
  set: (val: string | number | (string | number)[] | undefined) => {
    emit('update:modelValue', val)
  },
})

const selectClass = computed(() => {
  return normalizeClass([
    'select-custom',
    { borderless: props.borderless, 'soft-bordered': props.softBordered, rounded: props.rounded },
    props.class,
  ])
})

const containerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) {
    style.maxWidth = props.width
  }
  return style
})

const selectStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.height) {
    style.height = props.height
  }
  if (props.width) {
    style.width = props.width
  }
  return style
})

// Transform options to Ant Design format
const transformedOptions = computed(() => {
  if (!props.options || props.options.length === 0) {
    return []
  }

  // Check if first option already has 'label' and 'value' (already in Ant Design format)
  const firstOption = props.options[0]
  if ('label' in firstOption && 'value' in firstOption) {
    return props.options as SelectOption[]
  }

  // Transform from custom format to Ant Design format
  return props.options.map((item: SelectOption | Record<string, unknown>) => {
    const itemRecord = item as Record<string, unknown>
    const label = itemRecord[props.itemTitle] ?? itemRecord.label ?? itemRecord.name ?? String(item)
    const value = itemRecord[props.itemValue] ?? itemRecord.value ?? itemRecord.id ?? item
    return {
      label: String(label),
      value: typeof value === 'number' ? value : String(value),
      disabled: (itemRecord.disabled as boolean) ?? false,
    } as SelectOption
  })
})

// Merge props với attrs, props có priority cao hơn
const mergedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...restAttrs } = attrs
  return {
    ...restAttrs,
    placeholder: props.placeholder,
    disabled: props.disabled,
    size: props.size,
    options: transformedOptions.value,
    mode: props.mode,
    allowClear: props.allowClear,
    showSearch: props.showSearch,
    filterOption: props.filterOption,
    popupClassName: props.mode === 'multiple' ? 'select-dropdown-multiple' : undefined,
  }
})

const handleChange = (
  value: string | number | (string | number)[],
  option: SelectOption | SelectOption[],
) => {
  emit('change', value, option)
}

const handleBlur = (e: FocusEvent) => {
  emit('blur', e)
}

const handleSearch = (value: string) => {
  emit('search', value)
}
</script>

<style scoped lang="less">
:deep(.select-custom) {
  width: 100%;
  height: 40px;
  box-shadow: none !important;

  // Borderless mode (with background) - custom logic, keep
  &.borderless {
    .ant-select-selector {
      border: none !important;
      background: var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.05)) !important;
    }
  }

  // Borderless + the SDS hairline. Needs the extra `.borderless` in the
  // selector to outrank `&.borderless`'s `border: none`, which both this and
  // any consumer-side override would otherwise tie with and lose to.
  &.borderless.soft-bordered {
    .ant-select-selector {
      border: var(--sds-size-stroke-border, 1px) solid
        var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.1)) !important;
    }
  }

  // Rounded mode - add border radius
  &.rounded {
    .ant-select-selector {
      border-radius: 4px !important;
    }
  }

  // Not borderless - colors handled by theme, keep only custom logic
  &:not(.borderless) {
    .ant-select-selector {
      background: transparent !important;
    }
  }

  .ant-select-selection-overflow {
    margin-right: 24px !important;
  }

  // Selector
  .ant-select-selector {
    height: 100% !important;
    padding: 0 12px !important;
    display: flex !important;
    align-items: center !important;
    overflow: hidden !important; // Keep hidden for container, but allow content to show
    transition: border-color 0.2s ease !important;
    position: relative !important;
  }

  // Override for single mode - allow text to show without ellipsis
  &:not(.ant-select-multiple) {
    .ant-select-selection-search {
      overflow: visible !important;
    }
  }

  // When allowClear is enabled, add extra padding to prevent text overlap with clear icon
  &.ant-select-allow-clear {
    .ant-select-selector {
      padding-right: 56px !important; // 12px (base) + 16px (clear) + 8px (gap) + 16px (arrow) + 4px (extra spacing)
    }
  }

  .ant-select-selection-search {
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    flex: 1 !important;
    min-width: 0 !important;
    overflow: hidden !important;

    .ant-select-selection-search-input {
      height: 100% !important;
      line-height: 1 !important;
      width: 100% !important;
    }
  }

  .ant-select-selection-item {
    height: 100% !important;
    line-height: 40px !important;
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  // Multiple mode - selected tags
  &.ant-select-multiple {
    .ant-select-selection-item {
      height: 24px !important;
      line-height: 24px !important;
      display: inline-flex !important;
      align-items: center !important;
      background: var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1)) !important;
      border: 1px solid var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2)) !important;
      border-radius: 4px !important;
      padding: 0 8px !important;
      margin: 2px 4px 2px 0 !important;
      max-width: none !important;
      width: auto !important;
      min-width: 0 !important;
      flex-shrink: 0 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;

      .ant-select-selection-item-content {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 150px !important;
      }

      .ant-select-selection-item-remove {
        color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
        margin-left: 4px !important;
        cursor: pointer !important;
        display: inline-flex !important;
        align-items: center !important;
        flex-shrink: 0 !important;

        &:hover {
          color: var(--sds-color-text-default-primary, #ffffff) !important;
        }
      }
    }
  }

  .ant-select-selection-placeholder {
    height: 100% !important;
    line-height: 40px !important;
    display: block !important;
    opacity: 1 !important;
    width: 100% !important;
    min-width: 0 !important;
    color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
  }

  .ant-select-selection-overflow {
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    flex: 1 !important;
    min-width: 0 !important;
    // No overflow hidden for single mode
  }

  .ant-select-selection-overflow-item {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  // Focus state - keep box-shadow override
  &.ant-select-focused {
    .ant-select-selector {
      box-shadow: none !important;
    }
  }

  // Disabled state - keep opacity and cursor
  &.ant-select-disabled {
    opacity: 0.5;
    cursor: not-allowed;

    .ant-select-selector {
      cursor: not-allowed;
    }
  }

  // Selector needs relative positioning for absolute children
  .ant-select-selector {
    position: relative !important;
  }

  // Arrow icon - keep positioning and sizing, remove color (handled by theme)
  .ant-select-arrow {
    font-size: 12px !important;
    right: 12px !important;
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    width: 16px !important;
    height: 16px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
    vertical-align: middle !important;
  }

  // Rotate arrow icon when dropdown is open
  &.ant-select-open {
    .ant-select-arrow {
      transform: translateY(-50%) !important;

      .select-arrow-icon {
        transform: rotate(180deg) !important;
        transition: transform 0.3s ease !important;
      }
    }
  }

  // Default transition for arrow icon
  .ant-select-arrow {
    .select-arrow-icon {
      transition: transform 0.3s ease !important;
      transform-origin: center !important;
      display: block !important;
      width: 16px !important;
      height: 16px !important;
      margin: 0 !important;
      padding: 0 !important;
      flex-shrink: 0 !important;

      // Ensure SVG inside is centered
      svg {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
    }
  }

  // Clear icon - keep positioning, remove colors (handled by theme)
  .ant-select-clear {
    font-size: 12px !important;
    background: transparent !important;
    border-radius: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    right: 36px !important; // 12px (arrow right) + 16px (arrow width) + 8px (gap)
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    width: 16px !important;
    height: 16px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 1 !important;
  }

  // Remove margin and border-radius from input/search when allowClear is enabled
  .ant-select-selection-search {
    margin: 0 !important;
    border-radius: 0 !important;

    .ant-select-selection-search-input {
      margin: 0 !important;
      border-radius: 0 !important;
    }
  }
}

// Size variants - keep height and line-height, remove font-size (handled by theme)
:deep(.select-custom.ant-select-lg) {
  height: 48px;

  .ant-select-selector {
    height: 48px !important;

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 48px !important;
    }
  }
}

:deep(.select-custom.ant-select-sm) {
  height: 32px;

  .ant-select-selector {
    height: 32px !important;
    padding: 0 8px !important;

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 32px !important;
    }
  }
}

// Multiple mode
:deep(.select-custom.ant-select-multiple) {
  .ant-select-selector {
    padding: 0 12px !important;
    min-height: 40px !important;
    height: 40px !important;
    overflow: hidden !important;
    display: flex !important;
    align-items: center !important;

    .ant-select-selection-overflow {
      flex-wrap: nowrap !important;
      overflow: hidden !important;
      flex: 1 !important;
      min-width: 0 !important;
    }

    .ant-select-selection-overflow-item {
      width: auto !important;
      flex-shrink: 0 !important;
      // Ellipsis is handled in .ant-select-selection-item for multiple mode
    }

    .ant-select-selection-overflow-item-suffix {
      height: 100% !important;
      display: flex !important;
      align-items: center !important;
      flex-shrink: 0 !important;
    }

    .ant-select-selection-search {
      flex: 1 !important;
      min-width: 20px !important;
    }
  }
}

// Multiple mode with small size
:deep(.select-custom.ant-select-multiple.ant-select-sm) {
  height: 32px;

  .ant-select-selector {
    padding: 0 8px !important;
    min-height: 32px !important;
    height: 32px !important;
  }
}
</style>

// Dropdown styles (global, not scoped)
<style lang="less">
// Ant Design Select Dropdown
.ant-select-dropdown {
  background: rgba(26, 26, 26, 0.95) !important;
  backdrop-filter: blur(8px) !important;
  border: 1px solid var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2)) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5) !important;
  padding: 4px 0 !important;

  .ant-select-item {
    font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
    font-size: var(--sds-typography-scale-02, 14px) !important;
    color: var(--sds-color-text-default-primary, #ffffff) !important;
    padding: 8px 12px !important;
    line-height: 1.5 !important;
    transition: background-color 0.2s !important;

    &:hover:not(.ant-select-item-disabled) {
      background: var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1)) !important;
    }

    &.ant-select-item-option-selected {
      background: var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.15)) !important;
      color: var(--sds-color-text-default-primary, #ffffff) !important;
      font-weight: 500 !important;

      // Change checkmark icon color to brand-mint-green
      .ant-select-item-option-state,
      .anticon-check,
      .anticon-check-circle,
      svg {
        color: var(--sds-color-icon-brand-green, #00ff88) !important;
      }
    }

    &.ant-select-item-option-active {
      background: var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.1)) !important;
    }

    &.ant-select-item-disabled {
      color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
      opacity: 0.5 !important;
      cursor: not-allowed !important;
    }

    // No ellipsis and no max-width for single mode - remove default Ant Design ellipsis
    .ant-select-item-option-content {
      overflow: visible !important;
      text-overflow: clip !important;
      white-space: normal !important;
      max-width: none !important;
    }
  }

  // Only apply text width limitation for multiple mode
  // Only when dropdown has the multiple class
  &.select-dropdown-multiple {
    .ant-select-item {
      padding-right: 32px !important;
      display: flex !important;
      align-items: center !important;
      position: relative !important;

      .ant-select-item-option-content {
        flex: 1 !important;
        min-width: 0 !important;
        max-width: calc(100% - 24px) !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }

      .ant-select-item-option-state {
        position: absolute !important;
        right: 12px !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
        flex-shrink: 0 !important;
        width: 16px !important;
        height: 16px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
    }
  }

  .ant-select-item-empty {
    color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
    font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
    font-size: var(--sds-typography-scale-02, 14px) !important;
    padding: 8px 12px !important;
  }

  // Search input in dropdown
  .ant-select-dropdown-search {
    padding: 8px 12px !important;

    .ant-input {
      background: var(--sds-color-background-opacity-100, rgba(255, 255, 255, 0.05)) !important;
      border: 1px solid var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2)) !important;
      color: var(--sds-color-text-default-primary, #ffffff) !important;
      font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
      font-size: var(--sds-typography-scale-02, 14px) !important;

      &::placeholder {
        color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
        opacity: 1 !important;
      }

      &:focus {
        box-shadow: none !important;
      }
    }
  }

  // Empty state
  .ant-empty {
    color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
    font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
    font-size: var(--sds-typography-scale-02, 14px) !important;
  }
}
</style>
